/**
 * `useGlobalRevealHotkey` — wires the `?` (Shift+/) keyboard shortcut to
 * toggle "reveal all themed copy on this page". Mounted once at the app
 * root so it works everywhere.
 *
 * Ignores the keystroke when the user is typing in an input/textarea/
 * contenteditable so it doesn't interfere with form entry.
 */
import * as React from "react";
import { useCopyContext } from "@/theme/copy/CopyContext";

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (target.isContentEditable) return true;
  return false;
}

export function useGlobalRevealHotkey(): void {
  const { setForceRevealAll, forceRevealAll } = useCopyContext();

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "?") return;
      if (isTypingTarget(e.target)) return;
      e.preventDefault();
      setForceRevealAll(!forceRevealAll);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [forceRevealAll, setForceRevealAll]);
}
